import React, { useEffect } from "react";
import Card from "../../components/common/Card";
import ButtonOutline from "components/semantic/ButtonOutline";
import { PolicyCard } from "./components/PolicyCard";
import ReactPlayer from "react-player";
import { StatusBadge } from "./components/StatusBudge";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { getAllPoliciesThunk } from "store/thunk/kyp.thunk";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import underline from "assets/icons/underLine.svg";
import noFolder from "assets/images/noFolder.svg";
import { Skeleton } from "./components/Skeleton";
import { ALL_POLICY_TYPES } from "utils/utils";

const AllPolicy: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const policiesList = useAppSelector((state) => state.rootReducer.kypSlice.allPolicies);
  const isLoading = useAppSelector((state) => state.rootReducer.kypSlice.loadingPolicies);

  useEffect(() => {
    dispatch(getAllPoliciesThunk());
  }, []);

  const handleNavigation = async (id: any, qualityCheckStatus: string, isDetailedKyp: boolean, kypPaymentStatus: string, policyTypeId: any) => {
    const isQCCompleted = ["Quality Check Ready", "Quality Check Done"].includes(qualityCheckStatus);

    if (!isQCCompleted) {
      toast.success(<p className="text-[15px]">Your Quality Check Result will be available in 24 hours.</p>);
      return;
    }

    const isPaid = ["PAID", "WAIVEOFF"].includes(kypPaymentStatus);
    const isPending = kypPaymentStatus === "PENDING";

    const policyId = policyTypeId?._id;
    const isHealth = policyId === ALL_POLICY_TYPES.HEALTH;
    const isMotor = policyId === ALL_POLICY_TYPES.MOTOR;

    // MOTOR FLOW
    if (isMotor) {
      return navigate(isPaid ? `/kyp/kyp-motor-result/${id}` : `/kyp/motor-preview/${id}`);
    }

    // HEALTH FLOW
    if (isHealth) {
      if (isPaid) return navigate(`/kyp/kyp-health-result/${id}`);
      if (isPending) return navigate(`/kyp/policy-preview/${id}`);
    }

    // LIFE / OTHER FLOW
    if (isPaid) {
      return navigate(`/kyp/kyp-life-result/${id}`);
    }

    if (!isDetailedKyp && isPending) {
      return navigate(`/kyp/life-preview/${id}`);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold mb-6">Know Your Policy</h1>

      <Card className="w-full">
        <div className="hidden md:flex items-center px-6 md:px-10 py-4">
          <p className="font-bold text-lg">Safeguard yourself from future claim delays or rejections</p>
          <div className="ml-auto flex gap-4">
            <ButtonOutline title="Choose Your Product" className="w-52 border-black border-[1.5px] radius bg-none" onClick={() => navigate("/recommendation/products")} />
            <ButtonOutline title="Add New Policy" className="w-52 border-black border-[1.5px] radius bg-none" onClick={() => navigate("/kyp/upload-policy")} />
          </div>
        </div>
        

        <div className="bg-[#F8CA5926] block md:hidden border-[1.6px] border-[#0000001A] rounded-lg">
          <p className="font-bold text-[15px] text-[#00000099] p-5 pb-0">Safeguard yourself from future claim delays or rejections</p>
          <hr className="border-t border-dotted border-gray-400 mt-3" />
          <div className="px-5 py-3">
            <ButtonOutline title="Choose Your Product" className="bg-white" onClick={() => navigate("/recommendation/products")} />
            <ButtonOutline title="Add New Policy" className="bg-white" onClick={() => navigate("/kyp/upload-policy")} />
          </div>
        </div>
        <hr className="hidden md:block border-gray-200" />
        <div className="px-3 py-2">
          <div className="flex justify-center items-center py-6 w-full">
            <div className="w-full max-w-3xl aspect-video overflow-hidden rounded-lg border border-gray-300">
              <ReactPlayer url="https://www.youtube.com/watch?v=iVwPRBEEaGM" controls muted width="100%" height="100%" className="react-player" />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="px-3 sm:px-2 md:px-10 pb-10">
            <div className="grid grid-cols-1 half-lg:grid-cols-2 gap-6">
              {Array(4)
                .fill(0)
                .map((_, index) => (
                  <Skeleton key={index} />
                ))}
            </div>
          </div>
        ) : policiesList && policiesList.length === 0 ? (
          <div className="flex flex-col items-center justify-center bg-[#F4F5FA] border-2 rounded-lg mx-6 md:mx-10 mb-6 py-8">
            <div className="mb-4 flex items-center justify-center">
              <img src={noFolder} />
            </div>
            <p className="text-gray-500 text-lg font-medium">You don't have any policies yet</p>
          </div>
        ) : (
          <>
            <hr className="border-gray-200" />
            <div className="px-3 sm:px-2 md:px-10 pb-10">
              <div className="sm:flex sm:flex-col">
                <h2 className="text-lg font-bold mb-0 sm:mb-6 mt-4">Your Policies</h2>
                <img src={underline} alt="underLine img" className="block sm:hidden mb-6" />
              </div>
              <div className="grid grid-cols-1 half-lg:grid-cols-2 gap-6">
                {policiesList.map((policy: any) => (
                  <div className="flex flex-col" key={policy?._id} onClick={() => handleNavigation(policy?._id, policy?.qualityCheckStatus, policy?.isDetailedKyp, policy?.kypPaymentStatus, policy?.policyTypeId)}>
                    <StatusBadge type={policy.policyTypeId?.name} status={policy.qualityCheckStatus} />
                    <PolicyCard policy={policy} />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default AllPolicy;
